import React, {
    FunctionComponent,
    ReactElement,
    useState,
} from 'react';
import ReactJson from 'react-json-view';

// React bootstrap components
import { Navbar, Container } from "react-bootstrap";

// Material UI components
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import Button from '@mui/material/Button';

// Apis

// Types and constants
import { OptionModel } from '../../../../shared/types';
import { TUNE_BUTTON_IDS, } from '../../../../shared/constants';

// Components
import Separator from '../../../Separator/Separator';
import DeleteOptionsDialog from '../DeleteOptionsDialog/DeleteOptionsDialog';
import ErrorDialog from '../../../ErrorDialog/ErrorDialog';

type OptionsListProps = {
    optionList: OptionModel[];
    optionToDelete: OptionModel;
    openDeleteOptionDialog: boolean;
    openErrorDialog: boolean;
    error: any;
    addOption: (option: OptionModel) => void;
    saveOption: (option: OptionModel) => void;
    deleteOption: (optionType: string) => void;
    deleteOptionHandler: (option: OptionModel) => void;
    handleExpandCollapse: (option: OptionModel) => void;
    handleClose: () => void;
}

const OptionsList: FunctionComponent<OptionsListProps> = (props: OptionsListProps): ReactElement => {
    const {
        optionList,
        optionToDelete,
        openDeleteOptionDialog,
        openErrorDialog,
        error,
        addOption,
        saveOption,
        deleteOption,
        handleExpandCollapse,
        deleteOptionHandler,
        handleClose,
    } = props;

    const [optionToEdit, setOptionToEdit] = useState<OptionModel>(optionList[0]);
    const [enableSaveBtn, setEnableSaveBtn] = useState<boolean>(false);

    const onAddEditDeleteHandler = (e: any) => {
        setOptionToEdit(e.updated_src);
        setEnableSaveBtn(true);
    }

    const saveHandler = async () => {
        await saveOption(optionToEdit);
        setEnableSaveBtn(false);
    }

    const createOptionHandler = () => {
        const newOption: OptionModel = {
            id: '',
            type: 'new',
            values: {}
        };

        addOption(newOption);
        setOptionToEdit(newOption);
    }

    return (
        <div data-testid='option-list-container' className='align-center bottom-padding'>
            <Button
                data-testid={TUNE_BUTTON_IDS.CREATE_BTN}
                variant='outlined'
                onClick={createOptionHandler}
                startIcon={<AddIcon />}
            >
                Add Option
            </Button>
            <Separator />
            <Separator />
            <Navbar bg="light" expand="lg">
                <Container fluid>
                    <Navbar.Brand><b>Option List</b></Navbar.Brand>
                    <Navbar.Toggle aria-controls="navbarScroll" />
                    <Navbar.Collapse id="navbarScroll" className="justify-content-end">
                        <Button
                            data-testid='save-option-btn'
                            variant='contained'
                            color='success'
                            onClick={saveHandler}
                            disabled={!enableSaveBtn}
                        >Save
                        </Button>
                    </Navbar.Collapse>
                </Container>
            </Navbar>
            <List
                sx={{ width: '100%', maxWidth: '100vw', bgcolor: 'background.paper' }}
            >
                {
                    optionList.map((item: OptionModel, i) => {
                        return (
                            <>
                                <ListItemButton onClick={() => handleExpandCollapse(item)}>
                                    <ListItemIcon>
                                        <DirectionsBikeIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={item.type} />
                                    {item.isOpen ? <ExpandLess /> : <ExpandMore />}
                                    <Button
                                        data-testid={TUNE_BUTTON_IDS.CREATE_BTN}
                                        variant='outlined'
                                        color='error'
                                        onClick={() => deleteOptionHandler(item)}
                                        startIcon={<DeleteIcon />}
                                    />
                                </ListItemButton>

                                <Collapse in={item.isOpen} timeout="auto" unmountOnExit>
                                    <div className='align-left padding-left-lg'>
                                        <ReactJson
                                            src={item}
                                            name="option"
                                            onAdd={onAddEditDeleteHandler}
                                            onEdit={onAddEditDeleteHandler}
                                            onDelete={onAddEditDeleteHandler}
                                        />
                                    </div>

                                </Collapse>
                            </>
                        )
                    })
                }

            </List>

            {
                optionToDelete
                && (
                    <DeleteOptionsDialog
                        openDeleteOptionDialog={openDeleteOptionDialog}
                        optionToDelete={optionToDelete}
                        handleClose={handleClose}
                        deleteOption={deleteOption}
                    />
                )
            }
            {
                error &&
                (
                    <ErrorDialog
                        openErrorDialog={openErrorDialog}
                        handleClose={handleClose}
                        error={error}
                    />
                )
            }
        </div>
    );
};

export default OptionsList;
